/**
 * Factuur Detail - Gebaseerd op mooie Offerte layout
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { facturenService, Factuur } from '../../services/facturen.service';
import { relationsService, Relation } from '../../services/relations.service';
import { bedrijfsgegevensService, Bedrijfsgegevens } from '../../services/bedrijfsgegevens.service';
import VerzendModal from '../../components/VerzendModal';
import Toast from '../../components/Toast';
import '../../components/shared/briefhoofd.css';
import '../../components/shared/document.css';
import '../../components/shared/factuur-huisstijl.css';
import './FactuurDetail.css';

const FactuurDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [factuur, setFactuur] = useState<Factuur | null>(null);
  const [bedrijf, setBedrijf] = useState<Bedrijfsgegevens | null>(null);
  const [klant, setKlant] = useState<Relation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVerzendModal, setShowVerzendModal] = useState(false);
  const [verzending, setVerzending] = useState(false);
  const [verzendSuccess, setVerzendSuccess] = useState<string | null>(null);
  const [verzendError, setVerzendError] = useState<string | null>(null);

  // Betaling modal
  const [showBetalingModal, setShowBetalingModal] = useState(false);
  const [betalingData, setBetalingData] = useState({
    datum: new Date().toISOString().split('T')[0],
    bedrag: 0,
    betaalmethode: 'Bank',
    referentie: '',
    omschrijving: ''
  });

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  // Stel document title in voor correcte PDF bestandsnaam
  useEffect(() => {
    if (factuur) {
      document.title = `Factuur ${factuur.factuurnummer}`;
      return () => {
        document.title = 'Buro42';
      };
    }
  }, [factuur]);

  const loadData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);

      const [factuurData, bedrijfData] = await Promise.all([
        facturenService.getById(parseInt(id)),
        bedrijfsgegevensService.get()
      ]);

      setFactuur(factuurData);
      setBedrijf(bedrijfData);
      setBetalingData(prev => ({
        ...prev,
        bedrag: factuurData.bedrag_openstaand
      }));

      // Laad klantgegevens
      const klantData = await relationsService.getById(factuurData.relatie_id);
      setKlant(klantData);

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleVerzenden = () => {
    setShowVerzendModal(true);
  };

  const handleGenereerPDF = async () => {
    if (!factuur) return;
    
    try {
      // Gebruik gewoon server-side WeasyPrint PDF generatie
      const response = await facturenService.genereerPdf(factuur.id);
      
      if (response.message && response.pdf_path) {
        setVerzendSuccess(`PDF succesvol gegenereerd en opgeslagen in: ${response.pdf_path}`);
        setTimeout(() => setVerzendSuccess(null), 5000);
      } else {
        setError('Fout bij genereren PDF: Geen response ontvangen');
      }
      
    } catch (err: any) {
      console.error('PDF generatie fout:', err);
      setError('Fout bij genereren PDF: ' + (err.message || 'Onbekende fout'));
    }
  };

  const handleConfirmVerzenden = async (email: string) => {
    if (!factuur) return;
    
    try {
      setVerzending(true);
      setVerzendError(null);
      setVerzendSuccess(null);

      await facturenService.verstuur(factuur.id, email);
      await loadData();
      setShowVerzendModal(false);
      setVerzendSuccess('Factuur succesvol verzonden!');
      setTimeout(() => setVerzendSuccess(null), 5000);
    } catch (err: any) {
      setVerzendError(err.message || 'Fout bij verzenden factuur');
    } finally {
      setVerzending(false);
    }
  };

  const handleAddBetaling = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factuur) return;

    try {
      await facturenService.addBetaling(factuur.id, {
        datum: betalingData.datum,
        bedrag: betalingData.bedrag,
        betaalmethode: betalingData.betaalmethode || undefined,
        referentie: betalingData.referentie || undefined,
        omschrijving: betalingData.omschrijving || undefined
      });
      await loadData();
      setShowBetalingModal(false);
      setVerzendSuccess('Betaling geregistreerd');
      setTimeout(() => setVerzendSuccess(null), 3000);
    } catch (err: any) {
      setVerzendError(err.message || 'Kon betaling niet registreren');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    // Als het een heel getal is (bijv. 8.00), toon alleen het hele getal
    if (num % 1 === 0) {
      return Math.floor(num).toString();
    }
    // Anders toon met decimalen, maar verwijder onnodige nullen
    return num.toString().replace(/\.?0+$/, '');
  };

  const getKlantNaam = () => {
    if (!klant) return '';
    if (klant.bedrijfsnaam && klant.bedrijfsnaam !== 'Particulier') {
      return klant.bedrijfsnaam;
    }
    return [klant.voornaam, klant.tussenvoegsel, klant.achternaam].filter(Boolean).join(' ');
  };

  const getKlantAdres = () => {
    if (!klant) return '';
    return [klant.straat + (klant.huisnummer ? ' ' + klant.huisnummer : ''), `${klant.postcode} ${klant.plaats}`].filter(Boolean).join('\n');
  };

  /* Briefhoofd in de huisstijl: woordmerk in twee kleuren in plaats van het
     ronde logo, bedrijfsgegevens rechts. Gelijk aan factuur_weasyprint.html. */
  const renderHeader = () => {
    if (!bedrijf) return null;

    return (
      <div className="f42-kop">
        <div className="f42-woordmerk">
          <span className="buro">Buro</span><span className="vier">42</span>
        </div>
        <div className="f42-payoff">Het antwoord op alles</div>

        <div className="f42-bedrijf">
          <strong>{bedrijf.bedrijfsnaam}</strong>
          {bedrijf.straat && <div>{bedrijf.straat} {bedrijf.huisnummer || ''}</div>}
          {bedrijf.postcode && bedrijf.plaats && (
            <div>{bedrijf.postcode} {bedrijf.plaats}</div>
          )}
          <div className="ruimte"></div>
          {(bedrijf.telefoon || bedrijf.mobiel) && <div>{bedrijf.telefoon || bedrijf.mobiel}</div>}
          {bedrijf.email && <div>{bedrijf.email}</div>}
          {bedrijf.website && <div>{bedrijf.website}</div>}
          <div className="ruimte"></div>
          {bedrijf.kvk_nummer && <div>KvK {bedrijf.kvk_nummer}</div>}
          {bedrijf.iban && <div>IBAN {bedrijf.iban}</div>}
          {bedrijf.btw_nummer && <div>Btw-id {bedrijf.btw_nummer}</div>}
        </div>
      </div>
    );
  };

  const renderFactuurInfo = () => {
    if (!factuur || !klant) return null;

    return (
      <React.Fragment>
        <div className="f42-klant">
          <strong>{getKlantNaam()}</strong>
          {factuur.contactpersoon && <div>T.a.v. {factuur.contactpersoon}</div>}
          <div style={{ whiteSpace: 'pre-line' }}>{getKlantAdres()}</div>
        </div>

        <div className="f42-titel">Factuur</div>

        <div className="f42-meta">
          <div><span className="lbl">Factuurnummer</span>{factuur.factuurnummer}</div>
          <div><span className="lbl">Factuurdatum</span>{formatDate(factuur.factuur_datum)}</div>
          <div><span className="lbl">Vervaldatum</span>{formatDate(factuur.vervaldatum)}</div>
        </div>

        {factuur.onderwerp && (
          <div className="f42-betreft">Betreft: {factuur.onderwerp}</div>
        )}
      </React.Fragment>
    );
  };

  const renderWerkzaamheden = () => {
    if (!factuur) return null;

    return (
      <div className="f42-werk">
        <table className="f42-regels">
          <thead>
            <tr>
              <th className="c-aantal">Aantal</th>
              <th className="c-eenheid">Eenheid</th>
              <th>Omschrijving</th>
              <th className="c-prijs">Prijs p.e.</th>
              <th className="c-bedrag">Bedrag</th>
            </tr>
          </thead>
          <tbody>
            <tr className="f42-groep">
              <td colSpan={5}>Werkzaamheden</td>
            </tr>
            {factuur.regels.map((regel, index) => (
              <tr key={regel.id || index}>
                <td className="c-aantal">{formatNumber(regel.aantal)}</td>
                <td className="c-eenheid">{regel.eenheid || ''}</td>
                <td>{regel.omschrijving}</td>
                <td className="c-prijs">{formatCurrency(regel.prijs_per_eenheid)}</td>
                <td className="c-bedrag">{formatCurrency(regel.totaal)}</td>
              </tr>
            ))}
            <tr className="f42-subtotaal">
              <td></td>
              <td></td>
              <td colSpan={2}>Subtotaal excl. btw</td>
              <td className="c-bedrag"><div className="f42-streep"></div>{formatCurrency(factuur.subtotaal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  /* De drie vakjes onderin blijven; de bovenstreep is blauw, blauw, oranje. */
  const renderTotalen = () => {
    if (!factuur) return null;

    const isBtwVerlegd = factuur.btw_percentage === 0;

    return (
      <div className="f42-totalen">
        <div className="f42-vak">
          <div className="lbl">Totaalprijs excl. btw</div>
          <div className="bedrag">€ {formatCurrency(factuur.subtotaal)}</div>
        </div>
        <div className="f42-vak">
          <div className="lbl">
            {isBtwVerlegd ? 'Btw verlegd naar' : `Btw ${Math.round(factuur.btw_percentage)}%`}
          </div>
          <div className="bedrag">
            {isBtwVerlegd ? (bedrijf?.btw_nummer || '') : `€ ${formatCurrency(factuur.btw_bedrag)}`}
          </div>
        </div>
        <div className="f42-vak">
          <div className="lbl">Totaal incl. btw</div>
          <div className="bedrag">€ {formatCurrency(factuur.totaal)}</div>
        </div>
      </div>
    );
  };

  const renderBetalingsInfo = () => {
    if (!factuur || !bedrijf) return null;

    return (
      <React.Fragment>
        <div className="f42-lijn f42-voetlijn"></div>
        <div className="f42-betaalregel">
          Het openstaande bedrag van € {formatCurrency(factuur.totaal)} dient binnen{' '}
          {factuur.betalingstermijn || 30} dagen na factuurdatum te zijn overgemaakt op<br />
          {bedrijf.iban}, onder vermelding van factuurnummer {factuur.factuurnummer}.
        </div>
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="offerte-detail-container">
        <div className="loading">Factuur laden...</div>
      </div>
    );
  }

  if (error || !factuur) {
    return (
      <div className="offerte-detail-container">
        <div className="error-message">{error || 'Factuur niet gevonden'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/facturen')}>
          Terug naar Facturen
        </button>
      </div>
    );
  }

  return (
    <div className="offerte-detail-container">
      {/* Actie knoppen zoals offertes */}
      <div className="print-hide action-buttons">
        <button className="btn btn-purple" onClick={() => navigate(`/facturen/${factuur.id}/edit`)}>
          Bewerken
        </button>
        <button className="btn btn-success" onClick={handleGenereerPDF}>
          Opslaan als PDF
        </button>
        <button className="btn btn-secondary" onClick={handlePrint}>
          Afdrukken
        </button>
        <button className="btn btn-primary" onClick={handleVerzenden} disabled={verzending}>
          {verzending ? 'Verzenden...' : 'Verzenden'}
        </button>
      </div>

      {/* Success/Error Messages */}
      <Toast message={verzendSuccess} type="success" onClose={() => setVerzendSuccess(null)} />
      <Toast message={verzendError} type="error" onClose={() => setVerzendError(null)} />

      {/* Extra betalings/status management voor facturen */}
      <div className="print-hide">
        
        {(factuur.status === 'VERSTUURD' || factuur.status === 'DEELS_BETAALD') && factuur.bedrag_openstaand > 0 && (
          <div style={{
            backgroundColor: '#dcfce7',
            border: '1px solid #16a34a',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <button
              className="btn btn-success"
              onClick={() => {
                setBetalingData(prev => ({
                  ...prev,
                  bedrag: factuur.bedrag_openstaand
                }));
                setShowBetalingModal(true);
              }}
            >
              + Betaling Registreren ({formatCurrency(factuur.bedrag_openstaand)})
            </button>
          </div>
        )}

        {factuur.bedrag_openstaand === 0 && (
          <div style={{
            backgroundColor: '#dcfce7',
            border: '1px solid #16a34a',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            textAlign: 'center',
            color: '#166534'
          }}>
            <strong>✓ Factuur volledig betaald</strong>
          </div>
        )}
      </div>

      {/* Factuur Document (like offerte layout) */}
      <div className="offerte-document f42">
        {renderHeader()}
        {renderFactuurInfo()}
        {renderWerkzaamheden()}
        <div className="totalen-wrapper">
          {renderTotalen()}
          {renderBetalingsInfo()}
        </div>
      </div>

      {/* Betalingen overzicht als er betalingen zijn */}
      {factuur.betalingen && factuur.betalingen.length > 0 && (
        <div className="print-hide" style={{
          background: 'white',
          padding: '1.5rem',
          marginTop: '1rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h3>Betalingen</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Datum</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Bedrag</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Methode</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Referentie</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Acties</th>
              </tr>
            </thead>
            <tbody>
              {factuur.betalingen.map(betaling => (
                <tr key={betaling.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem' }}>{formatDate(betaling.datum)}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 500, color: '#059669' }}>
                    {formatCurrency(betaling.bedrag)}
                  </td>
                  <td style={{ padding: '0.75rem' }}>{betaling.betaalmethode || '-'}</td>
                  <td style={{ padding: '0.75rem' }}>{betaling.referentie || '-'}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={async () => {
                        if (window.confirm('Weet je zeker dat je deze betaling wilt verwijderen?')) {
                          try {
                            await facturenService.deleteBetaling(factuur.id, betaling.id!);
                            await loadData();
                            setVerzendSuccess('Betaling verwijderd');
                            setTimeout(() => setVerzendSuccess(null), 3000);
                          } catch (err: any) {
                            setVerzendError(err.message || 'Kon betaling niet verwijderen');
                          }
                        }
                      }}
                    >
                      Verwijderen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {/* Verzend Modal */}
      <VerzendModal
        isOpen={showVerzendModal}
        onClose={() => setShowVerzendModal(false)}
        onConfirm={handleConfirmVerzenden}
        onGenereerPDF={handleGenereerPDF}
        defaultEmail={klant?.email || ''}
        offertenummer={factuur.factuurnummer}
        isGeneratingPDF={false}
        type="factuur"
      />

      {/* Betaling Modal */}
      {showBetalingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowBetalingModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>Betaling Registreren</h2>
            <form onSubmit={handleAddBetaling}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Datum *</label>
                <input
                  type="date"
                  value={betalingData.datum}
                  onChange={e => setBetalingData({ ...betalingData, datum: e.target.value })}
                  required
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Bedrag *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={betalingData.bedrag}
                  onChange={e => setBetalingData({ ...betalingData, bedrag: parseFloat(e.target.value) || 0 })}
                  required
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Betaalmethode</label>
                <select
                  value={betalingData.betaalmethode}
                  onChange={e => setBetalingData({ ...betalingData, betaalmethode: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  <option value="Bank">Bank</option>
                  <option value="Contant">Contant</option>
                  <option value="iDEAL">iDEAL</option>
                  <option value="Creditcard">Creditcard</option>
                  <option value="Anders">Anders</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Referentie</label>
                <input
                  type="text"
                  value={betalingData.referentie}
                  onChange={e => setBetalingData({ ...betalingData, referentie: e.target.value })}
                  placeholder="Bijv. banktransactie ID"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowBetalingModal(false)}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Betaling Registreren
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FactuurDetail;